import { useEffect, useState } from "react";

import {
	isApiVerified,
	subscribeToVerification,
} from "@/api/api-verification-manager";
import { LoadingIndicator } from "@/components/loading-indicator/loading-indicator";
import { StatusMessage } from "@/components/status-messages/status-message";
import { useMe } from "@/features/users/hooks/useMe";
import { useLogout } from "@/hooks/useLogout";
import { useAuth } from "@/providers/auth-provider";

/**
 * Global loading state for Suspense tracking
 */
let suspenseLoadingCount = 0;
type LoadingListener = (isLoading: boolean) => void;
const loadingListeners: Set<LoadingListener> = new Set();

const notifyListeners = () => {
	const isLoading = suspenseLoadingCount > 0;
	loadingListeners.forEach((listener) => listener(isLoading));
};

// eslint-disable-next-line react-refresh/only-export-components -- Internal state management for SuspenseTracker
export const incrementSuspenseLoading = () => {
	suspenseLoadingCount++;
	notifyListeners();
};

// eslint-disable-next-line react-refresh/only-export-components -- Internal state management for SuspenseTracker
export const decrementSuspenseLoading = () => {
	suspenseLoadingCount = Math.max(0, suspenseLoadingCount - 1);
	notifyListeners();
};

/**
 * Full viewport loading overlay that shows until:
 * 1. API is verified (at least one successful API call) - only for authenticated users
 * 2. Suspense has resolved (route component loaded)
 */
export const LoadingOverlay = () => {
	const { authError, authUser, isAuthenticated, isLoadingAuth } = useAuth();
	const handleLogout = useLogout();
	const { isLoadingUser, isUserError, userError } = useMe();
	const [apiVerified, setApiVerified] = useState(isApiVerified);
	const [suspenseLoading, setSuspenseLoading] = useState(
		suspenseLoadingCount > 0,
	);

	useEffect(() => {
		return subscribeToVerification(setApiVerified);
	}, []);

	useEffect(() => {
		const listener = (isLoading: boolean) => setSuspenseLoading(isLoading);
		loadingListeners.add(listener);
		// Set initial state
		listener(suspenseLoadingCount > 0);
		return () => {
			loadingListeners.delete(listener);
		};
	}, []);

	// Auth loading is "complete" when the Kinde authentication is not loading anymore
	const authLoadingComplete = !isLoadingAuth;

	// Show Kinde authentication failure when the Kinde authentication is complete and there is an error
	const showKindeAuthFailure = authLoadingComplete && Boolean(authError);
	// When Kinde is done loading, authentication is success, the authUser is not null, and our internal user API call fails, show the profile fetch failure
	const showProfileFetchFailure =
		authLoadingComplete &&
		isAuthenticated &&
		Boolean(authUser?.id) &&
		isUserError;

	// Backend loading gates only apply once we know the user is signed in and Kinde has settled.
	// Guests skip these; they only wait on Suspense below.
	const signedInAndAuthSettled = isAuthenticated && authLoadingComplete;
	// With a Kinde user id, wait until the current-user profile query (`useUser`) finishes loading.
	const waitingOnCurrentUserProfile =
		signedInAndAuthSettled && Boolean(authUser?.id) && isLoadingUser;
	// Still blocking the shell when:
	// - a lazy route is in Suspense, or
	// - signed-in: first successful API call has not been recorded yet, or
	// - signed-in: current-user profile is still loading
	const waitingOnAppReady =
		suspenseLoading ||
		(signedInAndAuthSettled && !apiVerified) ||
		waitingOnCurrentUserProfile;

	// Show loader if:
	// 1. There is NOT a Kinde authentication failure
	// 2. There is NOT a profile fetch failure
	// 3. Waiting on app ready
	const showLoader =
		!showKindeAuthFailure && !showProfileFetchFailure && waitingOnAppReady;

	// This is needed to show the error message when the user is not authenticated or their profile is not loaded which is a very much edge case but happens when theres an issue with the Kinde API or the profile is not found in the database but the bearer token still exists.
	if (showKindeAuthFailure || showProfileFetchFailure) {
		const message = showKindeAuthFailure
			? authError
			: (userError?.message ??
				"Could not load your profile. Try signing out and back in.");

		return (
			<StatusMessage
				className="fixed inset-0 z-50 h-full max-h-full w-full max-w-full items-center justify-center bg-primary p-6"
				customButton={() => {
					void handleLogout();
				}}
				customButtonText="Start Over"
				description={message}
				fullHeight={false}
				theme="light"
				title={
					showKindeAuthFailure
						? "Authentication error"
						: "Unable to load profile"
				}
				type="error"
			/>
		);
	}

	if (!showLoader) {
		return null;
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-primary">
			<LoadingIndicator label="Loading" size="md" />
		</div>
	);
};

/**
 * Component to track Suspense loading state
 * Mount this as the Suspense fallback
 */
export const SuspenseTracker = () => {
	useEffect(() => {
		incrementSuspenseLoading();
		return () => {
			decrementSuspenseLoading();
		};
	}, []);

	// Render nothing - the LoadingOverlay handles the visual
	return null;
};
